import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import { setFormatSettings } from '../utils/numberFormat';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [company, setCompany] = useState(null);
  const [activePeriod, setActivePeriod] = useState(null);
  const [permissions, setPermissions] = useState([]);
  const [enabledModules, setEnabledModules] = useState([]);
  const [token, setToken] = useState(localStorage.getItem('erp_token'));
  const [loading, setLoading] = useState(true);
  const [theme, setTheme] = useState(localStorage.getItem('erp_theme') || 'light');

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem('erp_theme', theme);
  }, [theme]);

  const loadUser = useCallback(async () => {
    if (!token) { setLoading(false); return; }
    try {
      const res = await api.get('/auth/me');
      if (res.data.success) {
        setUser(res.data.data.user);
        setCompany(res.data.data.company);
        setActivePeriod(res.data.data.activePeriod || null);
        setPermissions(res.data.data.permissions || []);
        setEnabledModules(res.data.data.enabledModules || []);
        setTheme(res.data.data.user.theme || 'dark');
        // Format ayarlarını yükle (miktar/tutar ondalık sayısı)
        try {
          const sRes = await api.get('/settings');
          if (sRes.data.success) setFormatSettings(sRes.data.data);
        } catch {}
      }
    } catch (err) { logout(); }
    finally { setLoading(false); }
  }, [token]);

  useEffect(() => { loadUser(); }, [loadUser]);

  const login = async (email, password) => {
    const res = await api.post('/auth/login', { email, password });
    if (res.data.success) {
      const { token: t, user: u, company: c, activePeriod: ap, permissions: perms, enabledModules: em } = res.data.data;
      localStorage.setItem('erp_token', t);
      setToken(t); setUser(u); setCompany(c); setActivePeriod(ap || null); setPermissions(perms || []);
      setEnabledModules(em || []);
      setTheme(u.theme || 'dark');
      return { success: true };
    }
    return { success: false, message: res.data.message };
  };

  const logout = async () => {
    try { await api.post('/auth/logout'); } catch(e) {}
    localStorage.removeItem('erp_token');
    localStorage.removeItem('erp_token_original');
    setToken(null); setUser(null); setCompany(null); setActivePeriod(null); setPermissions([]); setEnabledModules([]);
  };

  // Impersonation: super_admin başka tenant'a giriyor
  const startImpersonation = (newToken) => {
    const original = localStorage.getItem('erp_token');
    if (original) localStorage.setItem('erp_token_original', original);
    localStorage.setItem('erp_token', newToken);
    // Sayfayı yenileyerek tüm context'i yeniden yükle (en güvenli yol)
    window.location.href = '/dashboard';
  };

  const endImpersonation = () => {
    const original = localStorage.getItem('erp_token_original');
    if (original) {
      localStorage.setItem('erp_token', original);
      localStorage.removeItem('erp_token_original');
      window.location.href = '/vendor-companies';
    } else {
      logout();
    }
  };

  // JWT'den impersonatedBy claim'ini oku
  const isImpersonating = (() => {
    if (!token) return false;
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      return !!payload.impersonatedBy;
    } catch { return false; }
  })();

  const impersonatedByInfo = (() => {
    if (!token) return null;
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      if (!payload.impersonatedBy) return null;
      return { adminId: payload.impersonatedBy, adminName: payload.impersonatedByName };
    } catch { return null; }
  })();

  const hasPermission = useCallback((perm) => {
    if (!permissions || permissions.length === 0) return false;
    if (permissions.includes('all')) return true;
    if (Array.isArray(perm)) return perm.some(p => permissions.includes(p));
    return permissions.includes(perm);
  }, [permissions]);

  // Modül aktif mi (super_admin her şeyi geçer)
  const hasModule = useCallback((modKey) => {
    if (!modKey) return true;
    if (user?.role === 'super_admin') return true;
    if (!enabledModules || enabledModules.length === 0) return modKey === 'core';
    return enabledModules.includes(modKey);
  }, [enabledModules, user]);

  const selectPeriod = async (periodId) => {
    try {
      const res = await api.post('/auth/select-period', { period_id: periodId });
      if (res.data.success) {
        const { token: t, activePeriod: ap } = res.data.data;
        localStorage.setItem('erp_token', t);
        setToken(t);
        setActivePeriod(ap);
        return { success: true };
      }
      return { success: false, message: res.data.message };
    } catch (err) {
      return { success: false, message: err.response?.data?.message || err.message };
    }
  };

  const toggleTheme = async () => {
    const t = theme === 'dark' ? 'light' : 'dark';
    setTheme(t);
    try { await api.put('/auth/profile', { theme: t }); } catch(e) {}
  };

  return (
    <AuthContext.Provider value={{ user, company, activePeriod, permissions, enabledModules, token, loading, theme, login, logout, toggleTheme, selectPeriod, hasPermission, hasModule, startImpersonation, endImpersonation, isImpersonating, impersonatedByInfo, isAuthenticated: !!token && !!user }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const c = useContext(AuthContext);
  if (!c) throw new Error('useAuth must be inside AuthProvider');
  return c;
}
